import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { storeToRefs } from 'pinia';

import { fetchRegisteredDeviceId } from '@/api/apiService';
import { useMic } from '@/composables/shared/useMic';
import { useAudioStore } from '@/stores/audio';
import { useCallStore } from '@/stores/call';
import { QRCodeType } from '@/stores/chat';
import { useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast.js';
import { stringToBase64UrlSafe } from '@/utils/encryptor.js';

export function useButtonToggleMic() {
  const { t } = useI18n();
  const isMicTurningOn = ref(false);
  const micOnAfterRegisterVoice = ref(false);

  const audioStore = useAudioStore();
  const callStore = useCallStore();
  const socketStore = useSocketStore();
  const toastStore = useToastStore();
  const { connectInfo } = storeToRefs(socketStore);
  const { qrCodeInfo, userInfo } = storeToRefs(callStore);
  const { isMutedByEmployee, isMicOn, selectedInput, isTssOn } =
    storeToRefs(audioStore);
  const {
    isMicReady,
    micAccess,
    micClear,
    micContextMap,
    micPrep,
    micStart,
    micStop,
  } = useMic();

  const isForcedMuteForMic = computed(
    () =>
      QRCodeType.isGuest(qrCodeInfo.value.qrCodeType) && isMutedByEmployee.value
  );

  const textButton = computed(() =>
    isMicTurningOn.value
      ? t('turning_mic_on')
      : isMicOn.value
        ? t('mic_on')
        : t('mic_off')
  );

  // 마이크 노출 여부
  const isVisibleMic = computed(() => {
    const qr = qrCodeInfo.value;
    const connectionType = connectInfo.value?.connectionType;
    const isGuest = QRCodeType.isGuest(qr?.qrCodeType);
    const showGuestMic = qr?.options?.showGuestMic;

    return !isGuest || (showGuestMic && connectionType !== 'SCREEN');
  });

  // 마이크 버튼 클릭 시
  const toggleMic = async () => {
    if (!isVisibleMic.value) return;
    if (isMutedByEmployee.value) {
      toastStore.warn(t('mic_muted_by_host'));
      return;
    }
    if (isMicTurningOn.value) return;

    if (!isMicOn.value && !isMicReady()) {
      isMicTurningOn.value = true;
      await audioStore.initDevices();
      await micPrep('streaming', selectedInput.value);
      isMicTurningOn.value = false;
    }
    audioStore.toggleMic();
  };

  const processAfterToggleMic = async (on: boolean) => {
    if (on) {
      await micStart('streaming', selectedInput.value);
    } else {
      const result = micStop('streaming');
      if (result && !micOnAfterRegisterVoice.value) {
        toastStore.info(t('toast_mic_off'), 'MIC');
      }
    }
  };

  const initProcess = async () => {
    if (!isVisibleMic.value) {
      audioStore.forceMicOff();
      return;
    }

    const micState = micContextMap.value['streaming']?.audioContext?.state;
    if (micState === ('interrupted' as AudioContextState)) {
      await micClear('streaming');
      await processAfterToggleMic(isMicOn.value);
    }

    if (!audioStore.isInputSelected()) {
      await audioStore.initDevices();
      if (!isMicReady() && audioStore.isInputSelected()) {
        await micPrep('streaming', selectedInput.value);
      } else {
        await micAccess();
      }
    }

    const isConsultationUser =
      QRCodeType.isConsultationType(qrCodeInfo.value.qrCodeType) &&
      userInfo.value.userType === 'GUEST';

    if (!isConsultationUser && audioStore.isInputSelected()) {
      const deviceLabel = selectedInput.value?.label;
      const { success, data } = await fetchRegisteredDeviceId(
        stringToBase64UrlSafe(deviceLabel)
      );
      if (success && data) {
        if (!isTssOn.value && userInfo.value.registeredDeviceId) {
          isTssOn.value = true;
          toastStore.info(t('toast_tss_on'), 'TSS');
        }
        callStore.updateDeviceId(data.deviceId);
      } else if (!data.deviceId) {
        toastStore.warn(t('employee_no_device_voice'), 'DEVICE');
      }
    }
  };

  return {
    isMicTurningOn,
    isForcedMuteForMic,
    textButton,
    isVisibleMic,
    toggleMic,
    initProcess,
    processAfterToggleMic,
  };
}
